import { createEllipsis } from "@bmi-digital/components/utils";
import CarouselHero from "@bmi-digital/components/carousel-hero";
import Search from "@bmi-digital/components/search";
import Section from "@bmi-digital/components/section";
import { microCopy } from "@bmi/microcopies";
import { graphql } from "gatsby";
import React from "react";
import { useTheme } from "@mui/material/styles";
import BackToResults from "../components/BackToResults";
import BrandLogo from "../components/BrandLogo";
import Breadcrumbs from "../components/Breadcrumbs";
import OverlapCards from "../components/OverlapCards";
import Page from "../components/Page";
import Sections from "../components/Sections";
import { useSiteContext } from "../components/Site";
import ButtonLink from "../components/link/ButtonLink";
import { updateBreadcrumbTitleFromPayload } from "../utils/breadcrumbUtils";
import { getPathWithCountryCode } from "../utils/path";
import createImageProps from "../components/image/createImageProps";
import createVideoProps from "../components/video/createVideoProps";
import type {
  Context as SiteContext,
  HomePageData,
  SiteData
} from "../components/Site";
import type { PageData } from "../components/Page";
import type { OverlapCard } from "../components/OverlapCards";
import type { Theme } from "@mui/material/styles";
import type { CarouselHeroItem } from "@bmi-digital/components/carousel-hero";
import type { LinkData as LinkData } from "../components/link/types";
import type { PromoHero } from "../components/Promo";
import type {
  BrandLandingPageInfoHero,
  PageInfoHero
} from "../components/PageInfo";
import type { MicroCopy as MicroCopyData } from "../components/MicroCopy";
import type { CalculatorConfig } from "../components/pitched-roof-calculator/types";
import type { HouseType } from "../components/visualiser/Types";
import type { GeneralErrorData } from "./general-error";
import type { CountryNavigation, SampleOrder } from "../components/Header";
import type { Visualiser } from "../components/Visualiser";
import type { PayloadBrandLandingPage } from "../types/payload";
import type { CardCollectionSectionData } from "../components/CardCollectionSection";
import type { CarouselSectionData } from "../components/carousel-section/CarouselSection";
import type { SyndicateSectionData } from "../components/SyndicateSection";
import type { SignupBlockSectionData } from "../components/SignupBlock";
import type { EmbeddedScriptSectionData } from "../components/EmbeddedScriptSection";
import type { BreadcrumbItem } from "../types/breadcrumb";
import type { TitleWithContentSectionData } from "../components/TitleWithContentSection";

type OverlapCardRelationship = { value: OverlapCard };

type BrandLandingPageData = Pick<
  PayloadBrandLandingPage,
  "longDescription" | "breadcrumbTitle"
> &
  PageData &
  BrandLandingPageInfoHero & {
    cta: readonly [LinkData];
    slides: readonly { value: PromoHero | PageInfoHero }[];
    overlapCards:
      | readonly [
          OverlapCardRelationship,
          OverlapCardRelationship,
          ...OverlapCardRelationship[]
        ]
      | null;
    sections: readonly (
      | CardCollectionSectionData
      | CarouselSectionData
      | SyndicateSectionData
      | TitleWithContentSectionData
      | SignupBlockSectionData
      | EmbeddedScriptSectionData
    )[];
  };

export type Props = {
  data: {
    payloadBrandLandingPage: BrandLandingPageData;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
  pageContext: {
    variantCodeToPathMap?: Record<string, string>;
  };
};

const getHeroItemsWithContext = (
  { getMicroCopy }: SiteContext,
  theme: Theme,
  slides: BrandLandingPageData["slides"]
): CarouselHeroItem[] => {
  const getCTAButton = (cta?: LinkData) => {
    return cta?.label ? (
      <ButtonLink data={cta} hasBrandColours>
        {cta?.label}
      </ButtonLink>
    ) : undefined;
  };

  const getCTALinkFromPath = (data: PageInfoHero) => {
    return "path" in data && data.path ? (
      <ButtonLink
        data={{
          blockType: "internal-link-block",
          label: data.title,
          icon: null,
          isLabelHidden: null,
          linkedPage: { value: { path: data.path } }
        }}
        hasBrandColours
      >
        {getMicroCopy(microCopy.PAGE_LINK_LABEL)}
      </ButtonLink>
    ) : undefined;
  };

  return slides.map((slide) => ({
    title: slide.value.title,
    children: "subtitle" in slide.value ? slide.value.subtitle : undefined,
    media:
      "featuredVideo" in slide.value && slide.value.featuredVideo
        ? createVideoProps(slide.value.featuredVideo, theme)
        : "featuredMedia" in slide.value && slide.value.featuredMedia
          ? createImageProps(
              {
                ...slide.value.featuredMedia,
                size: "cover"
              },
              theme
            )
          : undefined,
    cta:
      slide.value.collectionType === "promo"
        ? getCTAButton(slide.value.cta?.[0])
        : getCTALinkFromPath(slide.value)
  }));
};

const BrandLandingPageContent = ({
  brandLandingPageData,
  enhancedBreadcrumbs
}: {
  brandLandingPageData: BrandLandingPageData;
  enhancedBreadcrumbs: BreadcrumbItem[];
}) => {
  const siteContext = useSiteContext();
  const theme = useTheme();
  const heroItems = getHeroItemsWithContext(
    siteContext,
    theme,
    brandLandingPageData.slides
  );
  const { countryCode, getMicroCopy } = siteContext;

  const firstSlide: CarouselHeroItem = {
    title: brandLandingPageData.brandLogo ? (
      <BrandLogo
        brandName={brandLandingPageData.brandLogo}
        brandWhiteBox={true}
      />
    ) : undefined,
    children:
      brandLandingPageData.longDescription &&
      createEllipsis(brandLandingPageData.longDescription, 400),
    media: brandLandingPageData.featuredMedia
      ? createImageProps(
          {
            ...brandLandingPageData.featuredMedia,
            size: "cover",
            loading: "eager"
          },
          theme
        )
      : undefined,
    hasUnderline: false,
    cta:
      brandLandingPageData.cta && brandLandingPageData.cta[0] ? (
        <ButtonLink
          data={brandLandingPageData.cta[0]}
          data-testid="first-slide-cta"
          hasBrandColours
        >
          {brandLandingPageData.cta[0].label}
        </ButtonLink>
      ) : undefined
  };

  const overlapCards = brandLandingPageData.overlapCards?.map(
    (overlapCard) => overlapCard.value
  ) as [OverlapCard, OverlapCard, ...OverlapCard[]];

  return (
    <>
      <CarouselHero
        breadcrumbs={
          <BackToResults data-testid="breadcrumbs-section-top">
            <Breadcrumbs
              data={enhancedBreadcrumbs}
              data-testid="brand-landing-page-breadcrumbs-top"
            />
          </BackToResults>
        }
        heroes={[firstSlide, ...heroItems]}
      >
        <Search
          gtm={{
            id: "search2",
            label: getMicroCopy(microCopy.SEARCH_LABEL),
            action: getPathWithCountryCode(countryCode, "search")
          }}
          action={getPathWithCountryCode(countryCode, "search")}
          label={getMicroCopy(microCopy.SEARCH_LABEL)}
          placeholder={getMicroCopy(microCopy.SEARCH_PLACEHOLDER_HERO)}
          data-testid={"brand-search-form"}
        />
      </CarouselHero>
      {overlapCards && <OverlapCards data={overlapCards} />}
      {brandLandingPageData.sections && (
        <Sections data={brandLandingPageData.sections} />
      )}
      <Section
        backgroundColor="alabaster"
        isSlim
        data-testid="breadcrumbs-section-bottom"
      >
        <Breadcrumbs
          data={enhancedBreadcrumbs}
          data-testid="brand-landing-page-breadcrumbs-bottom"
        />
      </Section>
    </>
  );
};

const BrandLandingPage = ({ data, pageContext }: Props) => {
  const {
    title,
    brandLogo,
    featuredMedia,
    signupBlock,
    breadcrumbs,
    breadcrumbTitle,
    meta
  } = data.payloadBrandLandingPage;

  const enhancedBreadcrumbs = updateBreadcrumbTitleFromPayload(
    breadcrumbs,
    breadcrumbTitle
  );

  const pageData: PageData = {
    breadcrumbs: enhancedBreadcrumbs,
    signupBlock,
    meta,
    path: data.payloadBrandLandingPage.path
  };

  return (
    <Page
      brand={brandLogo}
      title={title}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
      ogImageUrl={featuredMedia?.sizes.heroDesktop.url}
      pageType="brandPage"
    >
      <BrandLandingPageContent
        brandLandingPageData={data.payloadBrandLandingPage}
        enhancedBreadcrumbs={enhancedBreadcrumbs}
      />
    </Page>
  );
};

export default BrandLandingPage;

export const pageQuery = graphql`
  query BrandLandingPageById($pageId: String!) {
    payloadBrandLandingPage(id: { eq: $pageId }) {
      description
      cta {
        ...LinkFragment
      }
      slides {
        ... on PayloadPromoOrPage {
          value {
            ...PromoHeroFragment
            ...PageInfoHeroFragment
          }
        }
      }
      overlapCards {
        value {
          ...OverlapCardFragment
        }
      }
      sections {
        ... on PayloadCardCollectionSection {
          ...CardCollectionSectionFragment
        }
        ... on PayloadCarouselSection {
          ...CarouselSectionFragment
        }
        ... on PayloadEmbeddedScriptSection {
          ...EmbeddedScriptSectionFragment
        }
        ... on PayloadSignupBlock {
          ...SignupBlockSectionFragment
        }
        ... on PayloadSyndicateSection {
          ...SyndicateSectionFragment
        }
        ... on PayloadTitleWithContent {
          ...TitleWithContentSectionFragment
        }
      }
      ...PageInfoHeroFragment
      ...PayloadBrandLandingPageFragment
      breadcrumbTitle
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
